/**
 * The welcome step in front of the driver invite (`/driver/accept?invite=…`).
 *
 * The invite link itself does not change: it stays the address the backend generates, the one the
 * app's App Link filter catches (mobile/app.json) and the one the login round trip returns to. The
 * welcome is only a first screen on that same address, so «Продолжить» swaps it for the existing
 * page without touching the URL — every parameter of the invite is still there.
 *
 * Who gets it: a guest holding an invite. A signed-in user already went through this once and goes
 * straight to the trip, and a driver with the app installed never reaches the web page at all.
 */

import { hasDriverInvite } from './openInApp'

// Per browser tab, and a flag rather than the invite itself: the code grants access to a trip, so
// it is not copied anywhere the URL does not already put it. Once seen, the welcome stays out of
// the way for the rest of the tab — a driver who backs out of the login page lands on the invite,
// not on the pitch again.
const CONTINUED_KEY = 'lupa.driverInviteWelcomeContinued'

export function hasContinuedWelcome(): boolean {
  try {
    return sessionStorage.getItem(CONTINUED_KEY) === '1'
  } catch {
    return false
  }
}

export function rememberWelcomeContinued(): void {
  try {
    sessionStorage.setItem(CONTINUED_KEY, '1')
  } catch {
    // Private mode — the only cost is seeing the welcome once more after a reload.
  }
}

export function shouldShowDriverInviteWelcome(state: {
  search: string
  authenticated: boolean
  initializing: boolean
  continued: boolean
}): boolean {
  // While Keycloak has not answered we do not know whether this is a guest: the existing page
  // handles that state (and the degraded one after the 8-second timeout) as it always did.
  if (state.initializing || state.authenticated || state.continued) return false
  return hasDriverInvite(state.search)
}

export type WelcomeFunnelStep = 'welcome_shown' | 'welcome_continue'

/**
 * Tells the server a guest reached a step of the welcome (driver invite funnel, see
 * backend/migrations/000023). Plain fetch on purpose: the shared `request` refreshes a Keycloak
 * session first and sends a guest to the login page when there is none. Fire and forget — the
 * driver never waits for it and never sees it fail.
 */
export function reportWelcomeStep(step: WelcomeFunnelStep, search: string): void {
  const params = new URLSearchParams(search)
  const body = {
    step,
    invite: params.get('invite') || params.get('code') || undefined,
    data: params.get('data') || undefined,
  }
  try {
    void fetch(`${import.meta.env.VITE_API_BASE || ''}/api/invite-funnel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {})
  } catch {
    // No fetch at all (an ancient in-app browser) — the funnel just misses this visit.
  }
}

/**
 * Size of the release APK in megabytes, as the file downloads (30.09.2026 build: 43 MB). Android
 * shows more once it is installed — it unpacks and compiles the code — which is why the page speaks
 * of the download. null makes the page drop every mention of the size.
 */
export const DRIVER_APP_SIZE_MB: number | null = 43
